import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Check if error is schema-related
const isSchemaError = (error) => {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
};

export const webhookIntegrationsService = {
  // Get all webhook integrations for a form
  async getWebhookIntegrations(formId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify user owns the form
      const { data: form } = await supabase?.from('forms')?.select('id')?.eq('id', formId)?.eq('user_id', user?.id)?.single();

      if (!form) throw new Error('Form not found or access denied');

      const { data, error } = await supabase?.from('webhook_integrations')?.select('*')?.eq('form_id', formId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching webhook integrations:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error in getWebhookIntegrations:', error);
      throw error;
    }
  },

  // Create webhook integration
  async createWebhookIntegration(formId, webhookData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Verify user owns the form
      const { data: form } = await supabase?.from('forms')?.select('id')?.eq('id', formId)?.eq('user_id', user?.id)?.single();

      if (!form) throw new Error('Form not found or access denied');

      const webhookToCreate = {
        ...toSnakeCase(webhookData),
        form_id: formId,
        created_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('webhook_integrations')?.insert(webhookToCreate)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error creating webhook integration:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error in createWebhookIntegration:', error);
      throw error;
    }
  },

  // Update webhook integration
  async updateWebhookIntegration(webhookId, webhookData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const webhookToUpdate = {
        ...toSnakeCase(webhookData),
        updated_at: new Date()?.toISOString()
      };

      const { data, error } = await supabase?.from('webhook_integrations')?.update(webhookToUpdate)?.eq('id', webhookId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error updating webhook integration:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error in updateWebhookIntegration:', error);
      throw error;
    }
  },

  // Delete webhook integration
  async deleteWebhookIntegration(webhookId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('webhook_integrations')?.delete()?.eq('id', webhookId);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error deleting webhook integration:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteWebhookIntegration:', error);
      throw error;
    }
  },

  // Test webhook by sending sample data
  async testWebhook(webhookId, testData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get webhook details
      const { data: webhook } = await supabase?.from('webhook_integrations')?.select('*, forms(title)')?.eq('id', webhookId)?.single();

      if (!webhook) throw new Error('Webhook not found');

      // Send test webhook
      const payload = {
        event: 'form.submitted',
        formId: webhook?.form_id,
        formName: webhook?.forms?.title || 'Test Form',
        submissionData: testData || { test: 'This is a test submission' },
        submittedAt: new Date()?.toISOString(),
        isTest: true
      };

      const response = await fetch(webhook?.webhook_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': 'WebForms.biz-Webhook/1.0'
        },
        body: JSON.stringify(payload)
      });

      if (!response?.ok) {
        throw new Error(`Webhook test failed: ${response?.status} ${response?.statusText}`);
      }

      return { success: true, message: 'Test webhook sent successfully' };
    } catch (error) {
      console.error('Error in testWebhook:', error);
      throw error;
    }
  },

  // Trigger webhook for a submission (called after form submission)
  async triggerWebhooks(formId, submissionData) {
    try {
      // Get all enabled webhooks for this form
      const { data: webhooks } = await supabase?.from('webhook_integrations')?.select('*')?.eq('form_id', formId)?.eq('enabled', true);

      if (!webhooks || webhooks?.length === 0) {
        console.log('No active webhooks for form:', formId);
        return { success: false, message: 'No active webhooks' };
      }

      // Get form details
      const { data: form } = await supabase?.from('forms')?.select('title')?.eq('id', formId)?.single();

      // Prepare webhook payload
      const payload = {
        event: 'form.submitted',
        formId: formId,
        formName: form?.title || 'Form',
        submissionData: submissionData,
        submittedAt: new Date()?.toISOString()
      };

      // Send to all webhooks asynchronously
      const webhookPromises = webhooks?.map(async (webhook) => {
        try {
          const response = await fetch(webhook?.webhook_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'User-Agent': 'WebForms.biz-Webhook/1.0'
            },
            body: JSON.stringify(payload)
          });

          if (!response?.ok) {
            console.error(`Webhook ${webhook?.id} failed:`, response?.status, response?.statusText);
            return { webhookId: webhook?.id, success: false, error: `${response?.status} ${response?.statusText}` };
          }

          return { webhookId: webhook?.id, success: true };
        } catch (error) {
          console.error(`Webhook ${webhook?.id} error:`, error);
          return { webhookId: webhook?.id, success: false, error: error?.message };
        }
      });

      const results = await Promise.all(webhookPromises);
      const successCount = results?.filter(r => r?.success)?.length;

      return {
        success: successCount > 0,
        message: `${successCount}/${webhooks?.length} webhooks triggered successfully`,
        results
      };
    } catch (error) {
      console.error('Error in triggerWebhooks:', error);
      return { success: false, error: error?.message };
    }
  }
};